import React, { useEffect, useRef, useState } from 'react';
import { get } from '../services/apiController';
import { TABLE_DEFAULT_LIMIT, DEBOUNCE_DELAY } from '../util/constant';
import { Form, Table, Row, Col } from 'react-bootstrap';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { GrDocumentExcel } from 'react-icons/gr';
import { SlRefresh } from 'react-icons/sl';
import PaginationInPage from './Pagination';
import { getStoredData, get_auth_key } from '../util';
import useDebounce from './useDebounce';
import Breadcrumb from '../components/Breadcrumb';
import Company from '../components/company';

const GrnReport = () => {
  const [totalRecords, setTotalRecords] = useState(0);
  const [locationList, setLocationList] = useState([]);
  const [assetCategoryList, setAssetCategoryList] = useState([]);
  const [filteredList, setFilteredList] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);

  const storedData = JSON.parse(getStoredData(get_auth_key()));
  const [locationType, setLocationType] = useState('');

  const admins = ['admin', 'super-admin', 'fixed-asset-admin'].includes(storedData.role);

  const [state, setState] = useState({
    locationQuery: '',
    assetCategoryQuery: '',
    companyId: !admins ? storedData?.companyId || '' : '',
  });
  const clickToDownload = useRef(null);

  function getLocations() {
    try {
      get(`/branches`).then((response) => {
        setLocationList(response?.data?.rows);
        if (!admins) {
          const data = response?.data?.rows?.find((item) => {
            return item.id === storedData?.locationId[0];
          });
          setLocationType(data?.type);
        }
      });
    } catch (err) {
      toast.error(err.response.data.data.message);
    }
  }

  function getAssetCategories() {
    try {
      get(`/asset-categories`).then((response) => {
        setAssetCategoryList(response.data.rows);
      });
    } catch (err) {
      toast.error(err.response.data.data.message);
    }
  }

  useEffect(() => {
    getLocations();
    getAssetCategories();
  }, []);

  const getFilteredAssetsInfo = (conditionType) => {
    try {
      if (conditionType === 'download') {
        get(
          `/assets/?locationId=${state?.locationQuery}&assetCategoryId=${state?.assetCategoryQuery}&conditionType=${conditionType}&companyId=${state.companyId}`
        ).then((response) => {
          clickToDownload.current.href = `${process.env.REACT_APP_BASE_URL}/file/download?filePath=${response.data.filePath}`;
          setTimeout(() => {
            clickToDownload.current.click();
          }, 500);
        });
      } else {
        get(
          `/assets/?page=${currentPage}&limit=${TABLE_DEFAULT_LIMIT}&locationId=${state?.locationQuery}&assetCategoryId=${state?.assetCategoryQuery}&conditionType=${conditionType}&companyId=${state.companyId}`
        ).then((response) => {
          if (conditionType !== 'download') {
            setFilteredList(response?.data?.rows);
            setTotalRecords(response?.data.count);
          }
        });
      }
    } catch (err) {
      toast.error(err.response.data.data.message);
    }
  };

  const handleRefresh = () => {
    if (!admins) {
      setLocationType('');
      setState({ ...state, locationQuery: '', assetCategoryQuery: '' });
    } else {
      setState({ ...state, assetCategoryQuery: '', locationQuery: '' });
    }
  };

  useDebounce(
    () => {
      if (currentPage !== 1) {
        setCurrentPage(1);
      } else {
        getFilteredAssetsInfo();
      }
    },
    [state],
    DEBOUNCE_DELAY
  );

  useEffect(() => {
    getFilteredAssetsInfo();
  }, [currentPage]);

  return (
    <React.Fragment>
      <ToastContainer />
      <Breadcrumb
        navigation={[
          { link: '#', title: 'Fixed Asset' },
          { link: '#', title: 'Reports' },
          { link: '#', title: 'Stock Registry' },
        ]}
      />
      <div className="card">
        <div className="card-header white-color">
          <h4 className="card-title mb-0" style={{ fontSize: '16px' }}>
            Available Stock | {totalRecords ?? 0}
          </h4>
          <div className="col-10">
            <div className="card-header-actions">
              <Row>
                <Col xs={2}>
                  <Company
                    disabled={!admins}
                    name="company"
                    company={state.companyId}
                    label="Company"
                    onChange={(e) => {
                      setState({ ...state, companyId: e.target.value, locationQuery: '' });
                      setLocationType('');
                    }}
                  />
                </Col>
                <Col xs={3}>
                  <Form.Select
                    disabled={!admins}
                    value={locationType}
                    onChange={(e) => {
                      setLocationType(e.target.value);
                      setState({ ...state, locationQuery: '' });
                    }}
                    className="input"
                  >
                    <option value="">Select Location Type</option>
                    <option value="office">Office</option>
                    <option value="factory">Factory</option>
                    <option value="outlet">Outlet</option>
                    <option value="staff-room">Staff Room</option>
                  </Form.Select>
                </Col>
                <Col xs={2}>
                  <Form.Select
                    onChange={(e) => {
                      setState({ ...state, locationQuery: e.target.value });
                    }}
                    value={state.locationQuery}
                    className="input"
                  >
                    <option value="">Select Location</option>
                    {locationList
                      .filter((item) => {
                        if (!admins) {
                          return (
                            storedData?.locationId.includes(item?.id) &&
                            item?.type === locationType &&
                            item?.companyId === parseInt(state?.companyId)
                          );
                        } else {
                          return item?.type === locationType && item?.companyId === parseInt(state?.companyId);
                        }
                      })
                      .map((item) => {
                        return (
                          <option key={item?.id} value={item?.id}>
                            {item?.name}
                          </option>
                        );
                      })}
                  </Form.Select>
                </Col>
                <Col xs={3}>
                  <Form.Select
                    onChange={(e) => setState({ ...state, assetCategoryQuery: e.target.value })}
                    className="input"
                    value={state.assetCategoryQuery}
                  >
                    <option value="">Select Asset Category</option>
                    {assetCategoryList.map((item) => {
                      return (
                        <option key={item?.id} value={item?.id}>
                          {item?.name}
                        </option>
                      );
                    })}
                  </Form.Select>
                </Col>
                <Col xs={1} className="d-flex align-items-center">
                  <SlRefresh
                    className="text-primary"
                    style={{ width: '70px', height: '30px', cursor: 'pointer' }}
                    onClick={() => handleRefresh()}
                  />
                </Col>
                <Col xs={1} className="d-flex align-items-center justify-content-end" style={{ paddingRight: '0' }}>
                  {totalRecords > 0 && (
                    <GrDocumentExcel
                      onClick={() => getFilteredAssetsInfo('download')}
                      style={{ width: '70px', height: '30px', cursor: 'pointer' }}
                      title="Download stock report"
                      className="react-icons green"
                    />
                  )}
                </Col>
              </Row>
            </div>
          </div>
        </div>
        <div className="card-footer">
          <div className="btn-wrapper ">
            {totalRecords === 0 && (
              <div className="container d-flex align-items-center justify-content-center">
                <h5 className="text-danger">No Records Found</h5>
              </div>
            )}
            <a ref={clickToDownload} href="/" target="_blank" className="hiddenItem">
              HiddenClick{' '}
            </a>
          </div>
        </div>
        {totalRecords > 0 && (
          <div className="card-body px-0 vehicle-requests-table pt-0 pb-0">
            <div className="row">
              <div className="col">
                <Table responsive striped bordered>
                  <thead>
                    <tr>
                      {/* <th>#</th> */}
                      <th>Location</th>
                      <th>Location Type</th>
                      <th>Asset Category</th>
                      <th>Asset Type</th>
                      <th>Asset Name</th>
                      <th>Asset Number</th>
                      <th>Stock</th>
                      <th>UoM</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredList?.map((item, index) => {
                      let rowspan = 1;
                      if (index > 0 && item?.Branch?.name === filteredList[index - 1]?.Branch?.name) {
                        rowspan = 0;
                      } else {
                        for (let i = index + 1; i < filteredList.length; i++) {
                          if (item?.Branch?.name === filteredList[i]?.Branch?.name) {
                            rowspan++;
                          } else {
                            break;
                          }
                        }
                      }
                      return (
                        <tr key={item?.id}>
                          {/* <td>{(currentPage - 1) * TABLE_DEFAULT_LIMIT + (index + 1)}</td> */}
                          {rowspan > 0 && (
                            <td style={{ verticalAlign: 'middle' }} rowSpan={rowspan}>
                              {item?.Branch?.name}
                            </td>
                          )}
                          {rowspan > 0 && (
                            <td rowSpan={rowspan} style={{ textTransform: 'capitalize', verticalAlign: 'middle' }}>
                              {item?.Branch?.type}
                            </td>
                          )}
                          <td>{item?.AssetCategory?.name}</td>
                          <td style={{ textTransform: 'upperCase' }}>{item?.AssetCategory?.type}</td>
                          <td>{item?.asset_name}</td>
                          <td>{item?.asset_number}</td>
                          <td>{item?.quantity === -1 ? 'LS' : item?.quantity}</td>
                          <td style={{ textTransform: 'capitalize' }}>{item?.unit}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
                <PaginationInPage
                  currentPage={currentPage}
                  setCurrentPage={setCurrentPage}
                  totalRecords={totalRecords}
                  TABLE_DEFAULT_LIMIT={TABLE_DEFAULT_LIMIT}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </React.Fragment>
  );
};

export default GrnReport;
